import { memo, useEffect, useRef, useState } from 'react'
import { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js'
import {
  analytics,
  analyticsEvent,
  analyticsEventLabel,
} from '../../../../common/utility/analytics'
import { WorkoutVideoPlayerProps } from '../../OnDemandWorkoutsDetail.d'
import { WorkoutVideoPlaceholder } from '../WorkoutVideoPlaceholder'
import { VideoJS } from '../VideoJS'

type WorkoutVideoProps = {
  isLoggedIn: boolean
} & WorkoutVideoPlayerProps

const { trackCustomEvent } = analytics
const { VIDEO } = analyticsEvent
const {
  WORKOUT_DETAILS: { PLAY, VOLUME_CHANGE, FULL_SCREEN },
} = analyticsEventLabel

const WorkoutVideoWhenLinkProvided = ({
  src,
  onPercentPlayedChange = () => {},
  onVideoEnded = () => {},
  ...props
}: WorkoutVideoPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasData, setHasData] = useState(false)
  const [duration, setDuration] = useState(0)
  const [timeElapsed, setTimeElapsed] = useState(0)
  const intervalRef = useRef(0)
  const playerRef = useRef<VideoJsPlayer | null>(null)

  const videoJsOptions: VideoJsPlayerOptions = {
    controls: true,
    fill: true,
    aspectRatio: '16:9',
    sources: [
      {
        src,
      },
    ],
  }

  const percentPlayed = (timeElapsed / duration) * 100 || 0

  const onTrackCustomEvent = (eventType: string) =>
    trackCustomEvent(VIDEO(eventType))

  useEffect(() => {
    if (isPlaying && hasData) {
      intervalRef.current = window.setInterval(() => {
        setTimeElapsed(timeElapsed + 1)
      }, 1000)
      onPercentPlayedChange(percentPlayed)
    } else {
      window.clearInterval(intervalRef.current)
    }

    return () => window.clearInterval(intervalRef.current)
  }, [isPlaying, hasData, timeElapsed, onPercentPlayedChange, percentPlayed])

  const handleOnPlay = () => {
    onTrackCustomEvent(PLAY)
    setIsPlaying(true)
    onVideoEnded(false)
  }

  const handlePlayerReady = (player: VideoJsPlayer) => {
    playerRef.current = player

    player.on('play', handleOnPlay)
    player.on('pause', () => setIsPlaying(false))
    player.on('ended', () => onVideoEnded(true))
    player.on('canplay', () => setHasData(true))
    player.on('waiting', () => setHasData(false))
    player.on('loadedmetadata', () => setDuration(player.duration()))
    player.on('volumechange', () => onTrackCustomEvent(VOLUME_CHANGE))
    player.on('fullscreenchange', () => onTrackCustomEvent(FULL_SCREEN))
  }

  return (
    <VideoJS options={videoJsOptions} onReady={handlePlayerReady} {...props} />
  )
}

const WorkoutVideo = ({
  src,
  isLoggedIn,
  onPercentPlayedChange = () => {},
  onVideoEnded = () => {},
  ...props
}: WorkoutVideoProps) => {
  const handleOnPercentPlayedChange = (percentChanged: number) =>
    onPercentPlayedChange(percentChanged)

  return src ? (
    <WorkoutVideoWhenLinkProvided
      src={src}
      onPercentPlayedChange={handleOnPercentPlayedChange}
      onVideoEnded={onVideoEnded}
      {...props}
    />
  ) : (
    <WorkoutVideoPlaceholder isLoggedIn={isLoggedIn} />
  )
}

export default memo(WorkoutVideo)
